import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useIsBonuslyCompany } from '@bonusly/who-am-i';

import { useNavbarPanelsStore } from '../../../hooks/use-navbar-panels';
import { resolvePageContext } from '../lib/bizy-page-config';
import { useBizyPageContextStore } from '../store/use-bizy-page-context-store';
import { useBizySidePanelStore } from '../store/use-bizy-side-panel-store';

interface UseBizyLayoutOptions {
	isMobile: boolean;
	isExpanded: boolean;
	setIsExpanded: (expanded: boolean) => void;
}

export function handleBizySlashKeyDown(event: KeyboardEvent, togglePanel: () => void) {
	if (event.key !== '/' || event.repeat || event.metaKey || event.ctrlKey) return;

	const target = event.target as HTMLElement | null;
	const tag = target?.tagName;
	const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable;
	if (isEditable) return;

	event.preventDefault();
	togglePanel();
}

/**
 * Encapsulates all Bizy side-panel layout concerns so AppLayout stays clean.
 *
 * Handles: company gating, store subscriptions, route-based page context,
 * homepage auto-open, sidebar auto-collapse on medium screens,
 * and Bizy auto-collapse when navbar panels open.
 */
export function useBizyLayout({ isMobile, isExpanded, setIsExpanded }: UseBizyLayoutOptions) {
	const isBonuslyCompany = useIsBonuslyCompany();
	const location = useLocation();

	const isPanelOpen = useBizySidePanelStore((s) => s.isOpen);
	const panelWidth = useBizySidePanelStore((s) => s.width);
	const isResizing = useBizySidePanelStore((s) => s.isResizing);
	const togglePanel = useBizySidePanelStore((s) => s.toggle);
	const setDefaultOpen = useBizySidePanelStore((s) => s.setDefaultOpen);
	const setPageContext = useBizyPageContextStore((s) => s.setContext);

	const isDesktop = isBonuslyCompany && !isMobile;
	const isHomepage = location.pathname === '/';

	const [isLargeDesktop, setIsLargeDesktop] = useState(() => {
		if (typeof window === 'undefined') return false;
		return window.matchMedia('(min-width: 1280px)').matches;
	});

	useEffect(() => {
		if (typeof window === 'undefined') return;
		const media = window.matchMedia('(min-width: 1280px)');
		const listener = (event: MediaQueryListEvent) => setIsLargeDesktop(event.matches);
		setIsLargeDesktop(media.matches);
		media.addEventListener('change', listener);

		return () => media.removeEventListener('change', listener);
	}, []);

	// Sync page context from current route
	useEffect(() => {
		if (!isBonuslyCompany || isMobile) return;
		setPageContext(resolvePageContext(location.pathname));
	}, [isBonuslyCompany, location.pathname, isMobile, setPageContext]);

	// Auto-open panel on homepage for large desktops (once per session)
	useEffect(() => {
		if (!isBonuslyCompany || isMobile || !isHomepage) return;
		setDefaultOpen(isLargeDesktop);
	}, [isBonuslyCompany, isHomepage, isLargeDesktop, isMobile, setDefaultOpen]);

	// Below lg: auto-collapse sidebar when Bizy opens, restore when it closes
	const wasExpandedBeforeBizyRef = useRef<boolean | null>(null);
	useEffect(() => {
		if (isLargeDesktop || isMobile) return;
		if (isPanelOpen) {
			if (isExpanded) {
				wasExpandedBeforeBizyRef.current = true;
				setIsExpanded(false);
			}
		} else if (wasExpandedBeforeBizyRef.current) {
			wasExpandedBeforeBizyRef.current = null;
			setIsExpanded(true);
		}
	}, [isPanelOpen, isLargeDesktop, isMobile]);

	// Auto-collapse Bizy when a navbar panel (Leaderboard, Team, etc.) opens; restore when it closes
	const openNavbarPanel = useNavbarPanelsStore((s) => s.openPanel);
	const wasBizyOpenBeforeSheetRef = useRef(false);
	useEffect(() => {
		if (isMobile) return;
		if (openNavbarPanel) {
			if (useBizySidePanelStore.getState().isOpen) {
				wasBizyOpenBeforeSheetRef.current = true;
				useBizySidePanelStore.getState().close();
			}
		} else if (wasBizyOpenBeforeSheetRef.current) {
			wasBizyOpenBeforeSheetRef.current = false;
			useBizySidePanelStore.getState().open();
		}
	}, [openNavbarPanel, isMobile]);

	// "/" toggles the Bizy panel (when not focused in an input/textarea/contenteditable)
	const handleKeyDown = useCallback((event: KeyboardEvent) => handleBizySlashKeyDown(event, togglePanel), [togglePanel]);

	useEffect(() => {
		if (!isBonuslyCompany || isMobile) return;
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isBonuslyCompany, isMobile, handleKeyDown]);

	return {
		isDesktop,
		isPanelOpen,
		panelWidth,
		isResizing,
		togglePanel,
	};
}
